/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { BaseService } from 'apps/console-core/src/Services/_base';
import { HttpResponse } from 'apps/console-core/src/Services/http';
import { Observable, of, switchMap } from 'rxjs';
import { EntityJob, EntityJobContext, EntityJobExecution, EntityJobManifest } from './Entities';
import { EntityJobExecutions } from './Entities/JobExecutions/JobExecutions.entity';
import { JobRunnerServiceConfiguration } from './JobRunner.service.config';
import {
  JobRunnerServiceBeginExecutionDto,
  JobRunnerServiceCreateContextDto,
  JobRunnerServiceDeleteContextDto,
  JobRunnerServiceGetContextDto,
  JobRunnerServiceGetJobDto,
  JobRunnerServiceGetJobExecutionDto,
  JobRunnerServiceGetJobExecutionsDto,
  JobRunnerServiceGetJobManifestDto,
  JobRunnerServiceTerminateExecutionDto,
  JobRunnerServiceUpdateContextDto,
} from './JobRunner.service.dto';

/**
 * The job runner service.
 * @export
 * @class JobRunnerService
 */
export class JobRunnerService extends BaseService {
  public constructor(configuration: JobRunnerServiceConfiguration) {
    super(configuration);
  }

  /**
   * Creates base api url for the Job Runner service.
   */
  getBaseServiceUrl(): string | null {
    // This service is deployed in multiple regions and
    // needs to use the tenant's region.
    return this.getServiceUrlByTenant(`job-runner`);
  }

  /**
   * Creates api url for jobs.
   */
  public createJobUrl(jobProperties?: { name: string; version: string }): string | null {
    const baseUrl = this.getBaseServiceUrl();

    if (!baseUrl) {
      return null;
    }

    if (jobProperties) {
      const { name, version } = jobProperties;
      return `${baseUrl}/job/${name}/${version}`;
    }

    return `${baseUrl}/job/latest`;
  }

  /**
   * Creates api url for job executions.
   */
  public createJobExecutionsBaseUrl(): string | null {
    const baseUrl = this.getBaseServiceUrl();
    if (!baseUrl) {
      return null;
    }

    return `${baseUrl}/executions`;
  }

  /**
   * Creates api url for getting a job execution or executions.
   */
  public createGetJobExecutionsUrl(name: string, executionId?: string): string | null {
    const baseExecutionsUrl = this.createJobExecutionsBaseUrl();
    if (!baseExecutionsUrl) {
      return null;
    }

    const executionsUrl = `${baseExecutionsUrl}/jobName/${name}`;
    return executionId ? `${executionsUrl}/id/${executionId}` : executionsUrl;
  }

  /**
   * Creates api url for starting a job execution.
   */
  public createBeginExecutionsUrl(): string | null {
    const baseExecutionsUrl = this.createJobExecutionsBaseUrl();
    if (!baseExecutionsUrl) {
      return null;
    }

    return `${baseExecutionsUrl}/begin`;
  }

  /**
   * Creates api url for terminating a job execution.
   */
  public createTerminateExecutionsUrl(): string | null {
    const baseExecutionsUrl = this.createJobExecutionsBaseUrl();
    if (!baseExecutionsUrl) {
      return null;
    }

    return `${baseExecutionsUrl}/terminate`;
  }

  /**
   * Creates api url for job contexts.
   */
  public createJobContextUrl(name?: string): string | null {
    const baseUrl = this.getBaseServiceUrl();

    if (!baseUrl) {
      return null;
    }

    return name ? `${baseUrl}/job-context/${name}` : `${baseUrl}/job-context`;
  }

  /**
   * Creates api url for job manifests.
   */
  public createJobManifestUrl(name: string, version: string): string | null {
    const baseUrl = this.getBaseServiceUrl();

    if (!baseUrl) {
      return null;
    }

    return `${baseUrl}/job-manifest/${name}/${version}`;
  }

  /**
   * Attempt to get a list of jobs (latest versions).
   */
  public getJobs(): Observable<Array<EntityJob> | null> {
    const queryParams: Array<string> = [];

    const baseJobUrl = this.createJobUrl();
    if (!baseJobUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const requestUrl = this.appendQueryParamsToUrl(queryParams, baseJobUrl);

    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<Array<EntityJob>> => {
          return this._handleResponseError<Array<EntityJob>>(value);
        }),
      );
  }

  /**
   * Attempt to get a specific job.
   */
  public getJob(dto: JobRunnerServiceGetJobDto): Observable<EntityJob | null> {
    const { name, version } = dto;

    const baseJobUrl = this.createJobUrl({ name, version });
    if (!baseJobUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .get<unknown, HttpResponse>(baseJobUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityJob> => {
          return this._handleResponseError<EntityJob>(value);
        }),
      );
  }

  /**
   * Attempt to get a list of job executions for a job.
   */
  public getJobExecutions(dto: JobRunnerServiceGetJobExecutionsDto): Observable<EntityJobExecutions | null> {
    const { name, pageAfter, pageBefore, pageSize } = dto;

    const queryParams: Array<string> = [];

    if (pageSize) {
      queryParams.push(`${encodeURIComponent('page[size]')}=${pageSize}`);
    }

    if (pageBefore) {
      queryParams.push(`${encodeURIComponent(`page[before]`)}=${pageBefore}`);
    }

    if (pageAfter) {
      queryParams.push(`${encodeURIComponent(`page[after]`)}=${pageAfter}`);
    }

    const baseExecutionsUrl = this.createGetJobExecutionsUrl(name);
    if (!baseExecutionsUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const requestUrl = this.appendQueryParamsToUrl(queryParams, baseExecutionsUrl);

    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityJobExecutions> => {
          return this._handleResponseError<EntityJobExecutions>(value);
        }),
      );
  }

  /**
   * Attempt to get a specific job execution by execution ID.
   */
  public getJobExecution(dto: JobRunnerServiceGetJobExecutionDto): Observable<EntityJobExecution | null> {
    const { name, executionId } = dto;

    const baseExecutionsUrl = this.createGetJobExecutionsUrl(name, executionId);
    if (!baseExecutionsUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .get<unknown, HttpResponse>(baseExecutionsUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityJobExecution> => {
          return this._handleResponseError<EntityJobExecution>(value);
        }),
      );
  }

  /**
   * Attempt to terminate a job execution.
   */
  public terminateJobExecution(dto: JobRunnerServiceTerminateExecutionDto): Observable<EntityJobExecution | null> {
    const baseExecutionsUrl = this.createTerminateExecutionsUrl();
    if (!baseExecutionsUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .post<unknown, HttpResponse>(baseExecutionsUrl, {
        ...this.getSessionRequestOptions(),
        body: dto,
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityJobExecution> => {
          return this._handleResponseError<EntityJobExecution>(value);
        }),
      );
  }
  /**
   * Attempt to begin a job execution.
   */
  public beginJobExecution(dto: JobRunnerServiceBeginExecutionDto): Observable<EntityJobExecution | null> {
    const baseExecutionsUrl = this.createBeginExecutionsUrl();
    if (!baseExecutionsUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .post<unknown, HttpResponse>(baseExecutionsUrl, {
        ...this.getSessionRequestOptions(),
        body: dto,
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityJobExecution> => {
          return this._handleResponseError<EntityJobExecution>(value);
        }),
      );
  }

  /**
   * Attempt to get a list of job contexts.
   */
  public getJobContexts(): Observable<Array<EntityJobContext> | null> {
    const baseContextUrl = this.createJobContextUrl();
    if (!baseContextUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .get<unknown, HttpResponse>(baseContextUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<Array<EntityJobContext>> => {
          return this._handleResponseError<Array<EntityJobContext>>(value);
        }),
      );
  }

  /**
   * Attempt to get a job context.
   */
  public getJobContext(dto: JobRunnerServiceGetContextDto): Observable<EntityJobContext | null> {
    const { contextName } = dto;

    const baseContextUrl = this.createJobContextUrl(contextName);
    if (!baseContextUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .get<unknown, HttpResponse>(baseContextUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityJobContext> => {
          return this._handleResponseError<EntityJobContext>(value);
        }),
      );
  }

  /**
   * Attempt to update a job context.
   */
  public updateJobContext(dto: JobRunnerServiceUpdateContextDto): Observable<EntityJobContext | null> {
    const { contextName, ...body } = dto;

    const baseContextUrl = this.createJobContextUrl(contextName);
    if (!baseContextUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .patch<unknown, HttpResponse>(baseContextUrl, {
        ...this.getSessionRequestOptions(),
        body,
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityJobContext> => {
          return this._handleResponseError<EntityJobContext>(value);
        }),
      );
  }

  /**
   * Attempt to create a job context.
   */
  public createJobContext(dto: JobRunnerServiceCreateContextDto): Observable<EntityJobContext | null> {
    const baseContextUrl = this.createJobContextUrl();
    if (!baseContextUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .post<unknown, HttpResponse>(baseContextUrl, {
        ...this.getSessionRequestOptions(),
        body: dto,
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityJobContext> => {
          return this._handleResponseError<EntityJobContext>(value);
        }),
      );
  }

  /**
   * Attempt to delete a job context.
   */
  public deleteJobContext(dto: JobRunnerServiceDeleteContextDto): Observable<null> {
    const { contextName } = dto;
    const baseContextUrl = this.createJobContextUrl(contextName);
    if (!baseContextUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .delete<unknown, HttpResponse>(baseContextUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<null> => {
          return this._handleResponseError<null>(value);
        }),
      );
  }

  /**
   * Attempt to get a job manifest.
   */
  public getJobManifest(dto: JobRunnerServiceGetJobManifestDto): Observable<EntityJobManifest | null> {
    const { name, version } = dto;
    const baseContextUrl = this.createJobManifestUrl(name, version);
    if (!baseContextUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .get<unknown, HttpResponse>(baseContextUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityJobManifest> => {
          return this._handleResponseError<EntityJobManifest>(value);
        }),
      );
  }
}
